import {Box} from '@mui/material';
import {has} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  IDocumentAudioFilePathItem,
  IDocumentAudioFilePathResponse,
} from '../../../../../api/types/document.type';
import {
  useAudioFilePath,
  useAudioWaveExits,
  useAudioWaveZoomInOut,
  useMediaForStreaming,
} from '../../../../../hooks/document/useDocument';
import {RootState} from '../../../../../redux/store';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import './DocumentMediaViewerPanel.scss';
import AVDocPreview from '../../../../components/avdoc-preview/AVDocPreview';
import {DocumentMediaViewerItem} from './DocumentMediaViewerItem';

export type FileTypeCode = 'ZoomOut' | 'ZoomIn';

const DocumentMediaViewerPanel: React.FC = () => {
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [fileTypeCode, setFileTypeCode] = useState<FileTypeCode>('ZoomOut');
  const previousDocumentIdRef = useRef<string>(undefined);
  const [mediaStreamingResponse, setMediaStreamingResponse] = useState<Blob>();
  const [videoMediaUrl, setVideoMediaUrl] = useState<string>();

  const activeDocument = useSelector(
    (state: RootState) => state.docView.activeDoc
  );

  const {
    isPending: audioFilePathLoading,
    data: audioFilePathResponse,
    mutate: fetchAudioFilePath,
  } = useAudioFilePath();

  const {
    data: audioWaveExistResponse,
    isPending: audioWaveExistLoading,
    mutate: fetchAudioWaveExits,
  } = useAudioWaveExits();

  const {
    isPending: audioWaveZoomInOutLoading,
    mutate: fetchAudioWaveZoomInOut,
  } = useAudioWaveZoomInOut();

  const {
    isPending: mediaStreamingLoading,
    mutateAsync: fetchMediaForStreaming,
  } = useMediaForStreaming();

  const isDifferentDocumentId = useMemo(
    () => activeDocument?.documentId !== previousDocumentIdRef.current,
    [activeDocument?.documentId]
  );

  const getWaveImageBlobResponse = useCallback(
    (blob: Blob) => {
      if (!blob) return;
      if (backgroundImageUrl) URL.revokeObjectURL(backgroundImageUrl);
      const url = URL.createObjectURL(blob);
      setBackgroundImageUrl(url);
    },
    [backgroundImageUrl]
  );

  const getAudioFilePathDetails = useCallback(
    (audioFileResponse: IDocumentAudioFilePathItem) => {
      return {
        ...(!has(audioFileResponse, 'zoomInFilePath') && {zoomInFilePath: ''}),
        ...(!has(audioFileResponse, 'transcriptionFilePath') && {
          transcriptionFilePath: '',
        }),
        ...audioFileResponse,
      };
    },
    []
  );

  const getVideo = useCallback(
    (fileResp: IDocumentAudioFilePathResponse) => {
      setVideoMediaUrl(undefined);
      const videoMediaPath = fileResp.videoFilePathDetails;
      if (videoMediaPath) {
        fetchMediaForStreaming(
          {mediaFilePath: videoMediaPath},
          {
            onSuccess: (blobResponse) => {
              setVideoMediaUrl(URL.createObjectURL(blobResponse));
            },
          }
        );
      }
    },
    [fetchMediaForStreaming]
  );

  useEffect(() => {
    if (videoMediaUrl) {
      URL.revokeObjectURL(videoMediaUrl);
    }

    if (!isDifferentDocumentId || !activeDocument?.documentId) return;

    previousDocumentIdRef.current = activeDocument?.documentId;
    setFileTypeCode('ZoomOut');

    if (backgroundImageUrl) {
      URL.revokeObjectURL(backgroundImageUrl);
      setBackgroundImageUrl('');
    }

    fetchAudioFilePath(
      {
        audioDocumentId: Number.parseInt(activeDocument?.documentId ?? '0'),
        playVideo: activeDocument?.internalVideoPlayerReady === '1' ? '1' : '0',
      },
      {
        onSuccess: (fileResp) => {
          if (!fileResp?.audioFilePathDetails) return;

          const details = {
            ...getAudioFilePathDetails(fileResp.audioFilePathDetails),
          } as IDocumentAudioFilePathItem;

          fetchAudioWaveExits(
            {
              mediaLength: Number.parseInt(
                activeDocument?.miniMediaLength ?? '0'
              ),
              audioFilePathDetails: details,
            },
            {
              onSuccess: (existResp) => {
                if (!existResp?.audioWaveExist) return;

                fetchAudioWaveZoomInOut(
                  {
                    fileTypeCode: 'ZoomOut',
                    audioFilePathDetails: details,
                  },
                  {
                    onSuccess: getWaveImageBlobResponse,
                  }
                );

                const mediaPath = fileResp.audioFilePathDetails.mediaFilePath;
                if (mediaPath) {
                  fetchMediaForStreaming(
                    {mediaFilePath: mediaPath},
                    {
                      onSuccess: (blobResponse) => {
                        setMediaStreamingResponse(blobResponse);
                        getVideo(fileResp);
                      },
                    }
                  );
                }
              },
            }
          );
        },
      }
    );
  }, [
    getAudioFilePathDetails,
    activeDocument?.documentId,
    isDifferentDocumentId,
  ]);

  useEffect(() => {
    return () => {
      if (backgroundImageUrl) {
        URL.revokeObjectURL(backgroundImageUrl);
      }
    };
  }, [backgroundImageUrl]);

  const getWaveImage = useCallback(
    (zoomType: FileTypeCode) => {
      if (
        !audioFilePathResponse?.audioFilePathDetails ||
        !audioWaveExistResponse?.audioWaveExist
      ) {
        return;
      }

      fetchAudioWaveZoomInOut(
        {
          fileTypeCode: zoomType,
          audioFilePathDetails: {
            ...getAudioFilePathDetails(
              audioFilePathResponse.audioFilePathDetails
            ),
          } as IDocumentAudioFilePathItem,
        },
        {
          onSuccess: getWaveImageBlobResponse,
        }
      );
    },
    [
      getAudioFilePathDetails,
      audioFilePathResponse?.audioFilePathDetails,
      audioWaveExistResponse?.audioWaveExist,
      fetchAudioWaveZoomInOut,
      getWaveImageBlobResponse,
    ]
  );

  const onChangeZoomType = useCallback(
    (zoomType: FileTypeCode) => {
      setFileTypeCode(zoomType);
      getWaveImage(zoomType);
    },
    [getWaveImage]
  );

  return (
    <Box className={'doc-media-container'}>
      <AVDocPreview
        waveformImageUrl={backgroundImageUrl}
        mediaUrl={mediaStreamingResponse}
        videoUrl={videoMediaUrl}
        zoomType={fileTypeCode}
        miniMediaLength={activeDocument?.miniMediaLength}
        miniAudioTrimmedLength={activeDocument?.miniAudioTrimmedLength}
        miniAudioDocDateTimeOnly={activeDocument?.miniAudioDocDateTimeOnly}
        onChangeZoomType={onChangeZoomType}
      >
        <DocumentMediaViewerItem
          audioFilePathDetails={
            audioFilePathResponse?.audioFilePathDetails
              ? getAudioFilePathDetails(
                  audioFilePathResponse?.audioFilePathDetails
                )
              : undefined
          }
        />
      </AVDocPreview>
      <ArrLoadingSpinner
        isOpen={
          audioFilePathLoading ||
          audioWaveExistLoading ||
          audioWaveZoomInOutLoading ||
          mediaStreamingLoading
        }
        overlay={true}
      />
    </Box>
  );
};

export {DocumentMediaViewerPanel};
